class SeatMapSeatTooltip {
    /**
     * @param {Object} options
     * @param {Object} options.mapState
     * @param {Element} options.seatElement
     * @param {HTMLElement} [options.wrapper]
     */
    constructor({mapState, seatElement, wrapper = null}) {
        this.mapState = mapState;
        this.wrapper = wrapper || mapState.wrapper;
        this.seatElement = seatElement;
        this.tooltipElement = null;
        this.popperInstance = null;

        this.handlePointerEnter = () => this.show();
        this.handlePointerLeave = () => this.hide();
    }

    bind() {
        if (this.isEnabled() === false) {
            return;
        }

        this.seatElement.addEventListener('pointerenter', this.handlePointerEnter);
        this.seatElement.addEventListener('pointerleave', this.handlePointerLeave);
    }

    isEnabled() {
        return this.wrapper instanceof HTMLElement && this.wrapper.dataset.seatTooltipShows === 'true';
    }

    show() {
        if (this.isEnabled() === false) {
            return;
        }

        if (!this.tooltipElement) {
            this.tooltipElement = this.createTooltipElement();
            this.wrapper.appendChild(this.tooltipElement);
        }

        this.tooltipElement.classList.remove('d-none');

        if (!this.popperInstance && window.Popper && typeof window.Popper.createPopper === 'function') {
            this.popperInstance = window.Popper.createPopper(
                this.seatElement,
                this.tooltipElement,
                {
                    placement: 'top',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 12],
                            },
                        },
                    ],
                },
            );
        }

        if (this.popperInstance && typeof this.popperInstance.update === 'function') {
            this.popperInstance.update();
        }
    }

    hide() {
        if (this.tooltipElement) {
            this.tooltipElement.classList.add('d-none');
        }
    }

    destroy() {
        this.seatElement.removeEventListener('pointerenter', this.handlePointerEnter);
        this.seatElement.removeEventListener('pointerleave', this.handlePointerLeave);

        if (this.popperInstance && typeof this.popperInstance.destroy === 'function') {
            this.popperInstance.destroy();
        }

        if (this.tooltipElement) {
            this.tooltipElement.remove();
        }

        this.popperInstance = null;
        this.tooltipElement = null;
    }

    createTooltipElement() {
        const tooltipElement = document.createElement('div');

        tooltipElement.className = 'seat-map-seat-tooltip d-none';
        tooltipElement.setAttribute('role', 'tooltip');

        const bodyElement = document.createElement('div');
        bodyElement.className = 'seat-map-seat-tooltip-body';

        this.getEntries().forEach(entry => {
            bodyElement.appendChild(this.createEntryElement(entry));
        });

        const arrowElement = document.createElement('div');
        arrowElement.className = 'seat-map-seat-tooltip-arrow';
        arrowElement.setAttribute('data-popper-arrow', '');

        tooltipElement.appendChild(bodyElement);
        tooltipElement.appendChild(arrowElement);

        return tooltipElement;
    }

    getEntries() {
        const entries = [];

        if (this.wrapper.dataset.seatTooltipShowsSection === 'true') {
            entries.push({
                key: 'section',
                label: this.wrapper.dataset.seatTooltipSectionLabel || '',
                value: this.seatElement.dataset.tooltipSection || '',
            });
        }

        if (this.wrapper.dataset.seatTooltipShowsRow === 'true') {
            entries.push({
                key: 'row',
                label: this.wrapper.dataset.seatTooltipRowLabel || '',
                value: this.seatElement.dataset.tooltipRow || '',
            });
        }

        entries.push({
            key: 'seat',
            label: this.wrapper.dataset.seatTooltipSeatLabel || '',
            value: this.seatElement.dataset.tooltipSeat || '',
        });

        return entries;
    }

    createEntryElement(entry) {
        const entryElement = document.createElement('div');
        entryElement.className = `seat-map-seat-tooltip-entry seat-map-seat-tooltip-entry-${entry.key}`;

        if (entry.label !== '') {
            const labelElement = document.createElement('div');
            labelElement.className = 'seat-map-seat-tooltip-label';
            labelElement.textContent = entry.label;
            entryElement.appendChild(labelElement);
        }

        const valueElement = document.createElement('div');
        valueElement.className = 'seat-map-seat-tooltip-value';
        valueElement.textContent = entry.value;
        entryElement.appendChild(valueElement);

        return entryElement;
    }
}

export {
    SeatMapSeatTooltip
};
